import { Box, CircularProgress, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts";
import { useStrategyQuery } from "hooks/useStrategyQuery";
import { useAccountQuery } from "hooks/useAccountQuery";
import { useGetReportQuery } from "../../../features/trading/accounts";
import { useReportTrigger } from "../../../containers/ReportPageDeprecated";
import { useMemo } from "react";

const PerformanceChart = () => {
  const { data: stockData } = useStrategyQuery();
  const { data: accountData } = useAccountQuery();
  const { shouldFetch, columnPeriods } = useReportTrigger();

  // Create query parameters - use the same periods as IncomeSummary
  const queryParams = useMemo(() => {
    if (
      stockData?.id &&
      accountData?.id &&
      shouldFetch &&
      columnPeriods.length > 0
    ) {
      return {
        account_ids: [accountData.id],
        strategy_ids: [stockData.id],
        periods: columnPeriods.map((col) => ({
          name: col.name,
          period: col.period,
        })),
      };
    }
    return null;
  }, [stockData?.id, accountData?.id, columnPeriods, shouldFetch]);

  // Use the query hook
  const {
    data: reportData,
    isLoading,
    error,
  } = useGetReportQuery(queryParams!, {
    skip: !queryParams,
  });

  // Memoize the chart data
  const chartData = useMemo(() => {
    if (reportData?.data?.graph_data && reportData.data.graph_data.length > 0) {
      const graphData = reportData.data.graph_data;

      // Create a copy of the array before sorting to avoid modifying the read-only original
      const sortedData = [...graphData].sort(
        (a: any, b: any) =>
          new Date(a.date).getTime() - new Date(b.date).getTime(),
      );

      return {
        dates: sortedData.map((item: any) => new Date(item.date)),
        stockValues: sortedData.map((item: any) => item.stock_value),
        optionValues: sortedData.map((item: any) => item.option_value),
      };
    }
    return null;
  }, [reportData?.data?.graph_data]);

  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 400,
          mt: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 400,
          mt: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography color="error">
          Error loading chart data. Please try again.
        </Typography>
      </Box>
    );
  }

  if (!chartData) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 400,
          mt: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography color="text.secondary">
          No chart data available. Click "Get" to load data.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: 400, mt: 2 }}>
      <LineChart
        series={[
          {
            data: chartData.optionValues,
            label: `${stockData?.name || "Stock"} Optic`,
            color: "#E4C6B2",
            showMark: false,
          },
          {
            data: chartData.stockValues,
            label: `${stockData?.name || "Stock"} Stock`,
            color: "#645028",
            showMark: false,
          },
        ]}
        xAxis={[
          {
            data: chartData.dates,
            scaleType: "time",
            valueFormatter: (date: Date) => date.toLocaleDateString(),
          },
        ]}
        height={300}
        margin={{ top: 10, bottom: 30, left: 40, right: 10 }}
      />
    </Box>
  );
};

export default PerformanceChart;
